import React from 'react';
import { Check } from 'lucide-react';

const Ingredients = () => {
  const ingredients = [
    {
      name: 'Pumpkin Seeds',
      image: 'https://images.unsplash.com/photo-1632046560046-af4b05a47300?crop=entropy&cs=srgb&fm=jpg&q=85',
      benefits: [
        'High in protein (7g per 30g serving)',
        'Rich in zinc for immune health',
        'Contains magnesium for bone health',
        'Source of healthy omega-3 fatty acids'
      ]
    },
    {
      name: 'Sunflower Seeds',
      image: 'https://images.unsplash.com/photo-1625558904461-6cf9d0a18a18?crop=entropy&cs=srgb&fm=jpg&q=85',
      benefits: [
        'Excellent source of vitamin E',
        'Supports heart health',
        'Anti-inflammatory properties',
        'High in selenium'
      ]
    },
    {
      name: 'Sesame Seeds',
      image: 'https://images.unsplash.com/photo-1653621709400-71c19e495ef1?crop=entropy&cs=srgb&fm=jpg&q=85',
      benefits: [
        'Rich in calcium for strong bones',
        'Contains healthy fats',
        'Source of copper and manganese',
        'May help lower cholesterol'
      ]
    },
    {
      name: 'Flax Seeds',
      image: 'https://images.unsplash.com/photo-1632046560046-af4b05a47300?crop=entropy&cs=srgb&fm=jpg&q=85',
      benefits: [
        'Loaded with omega-3 fatty acids',
        'High in dietary fiber',
        'Contains lignans with antioxidant properties',
        'May improve digestive health'
      ]
    },
    {
      name: 'Natural Dates',
      image: 'https://images.unsplash.com/photo-1625558904461-6cf9d0a18a18?crop=entropy&cs=srgb&fm=jpg&q=85',
      benefits: [
        'Natural sweetness, no refined sugar',
        'Rich in potassium',
        'Quick energy source',
        'Contains essential minerals'
      ]
    },
    {
      name: 'Dark Chocolate',
      image: 'https://images.unsplash.com/photo-1653621709400-71c19e495ef1?crop=entropy&cs=srgb&fm=jpg&q=85',
      benefits: [
        'Packed with antioxidants',
        'May improve heart health',
        'Contains beneficial minerals',
        'Enhances flavor naturally'
      ]
    }
  ];

  return (
    <div data-testid="ingredients-page">
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-b from-background to-muted" data-testid="ingredients-hero">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">The Power of Super Seeds</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Every ingredient in Seedoza is carefully selected for its exceptional nutritional value and health benefits.
          </p>
        </div>
      </section>

      {/* Ingredients Grid */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="space-y-16">
            {ingredients.map((ingredient, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
                data-testid={`ingredient-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="aspect-video rounded-3xl overflow-hidden shadow-hover">
                    <img
                      src={ingredient.image}
                      alt={ingredient.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''} className="space-y-6">
                  <h2 className="text-4xl font-heading font-bold">{ingredient.name}</h2>
                  <div className="space-y-3">
                    {ingredient.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <Check className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-lg text-muted-foreground">{benefit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Seeds Section */}
      <section className="section-spacing bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-heading font-bold">Why Seeds?</h2>
            <p className="text-xl leading-relaxed">
              Seeds are nature's nutritional powerhouses. Despite their small size, they're packed with protein, healthy fats, fiber, vitamins, and minerals. They provide sustained energy without blood sugar spikes, making them perfect for active lifestyles.
            </p>
            <p className="text-xl leading-relaxed">
              By combining five different super seeds, we've created a complete nutritional profile that delivers maximum benefits in every bite.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-heading font-bold mb-6">Experience the Difference</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Try Seedoza Energy Bites and feel the natural power of super seeds.
          </p>
          <a href="/products">
            <button className="btn-accent" data-testid="ingredients-cta-button">
              Shop Now
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Ingredients;
